import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { trials } from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Briefcase, Calendar } from 'lucide-react';

// Human-readable labels + colours for every TrialStatus value (backend trial_payments.py)
const STATUS_BADGES = {
    pending_candidate_response: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Awaiting Response' },
    pending_payment: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending Payment' },
    payment_failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Payment Failed' },
    scheduled: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Scheduled' },
    in_progress: { bg: 'bg-green-100', text: 'text-green-800', label: 'In Progress' },
    completed: { bg: 'bg-teal-100', text: 'text-teal-800', label: 'Completed' },
    terminated_early: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Terminated Early' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled' },
    cancelled_late: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled (Late)' },
    cancelled_by_employer: { bg: 'bg-red-100', text: 'text-red-800', label: 'Cancelled by Employer' },
    candidate_no_show: { bg: 'bg-red-100', text: 'text-red-800', label: 'No Show' },
    expired: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Expired' },
};

const statusBadge = (status) => STATUS_BADGES[status] || { bg: 'bg-slate-100', text: 'text-slate-600', label: (status || '').replace(/_/g, ' ') };

const TrialsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [myTrials, setMyTrials] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadTrials();
    }, []);

    const loadTrials = async () => {
        try {
            const res = await trials.getMyBookings();
            const bookings = res.data?.bookings;
            setMyTrials(Array.isArray(bookings) ? bookings : []);
        } catch (error) {
            if (error.response?.status === 404) {
                setMyTrials([]);
            } else {
                toast.error('Failed to load trials');
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <header className="border-b border-purple-200/50 bg-white">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                        <h1 className="text-2xl font-bold" style={{fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A'}}>Sampler</h1>
                    </div>
                    <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-600 hover:text-[#8B5CF6] hover:bg-purple-50" data-testid="back-btn">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Back
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                <h1 className="text-4xl font-bold mb-8" style={{fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A'}}>Trials</h1>

                {myTrials.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center border border-purple-200/50" data-testid="trials-empty">
                        <Calendar className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                        <h3 className="text-xl font-bold mb-2" style={{ color: '#0A0A0A' }}>No trials yet</h3>
                        <p className="text-slate-500">Your trial invitations and active trials will appear here</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4" data-testid="trials-list">
                        {myTrials.map(trial => {
                            const badge = statusBadge(trial.status);
                            return (
                                <div key={trial.id} className="bg-white rounded-xl p-6 border border-purple-200/50" data-testid={`trial-item-${trial.id}`}>
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <h3 className="text-xl font-bold mb-2" style={{ color: '#0A0A0A' }}>{trial.job_title || 'Position'}</h3>
                                            <p className="text-slate-500 mb-3">
                                                {user.user_type === 'company' ? trial.candidate_name : trial.company_name}
                                            </p>
                                            <div className="flex items-center space-x-4 text-sm flex-wrap gap-y-1">
                                                <span className="font-semibold" style={{ color: '#7C3AED' }}>
                                                    {trial.start_date ? new Date(trial.start_date).toLocaleDateString('en-GB') : '—'}
                                                </span>
                                                {trial.start_time && (
                                                    <span className="text-slate-500">{trial.start_time} (UK time)</span>
                                                )}
                                                <span className="text-slate-500">
                                                    {trial.trial_days} day{trial.trial_days > 1 ? 's' : ''}
                                                </span>
                                                {trial.location && (
                                                    <span className="text-slate-500">{trial.location}</span>
                                                )}
                                            </div>
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`} data-testid={`trial-status-${trial.id}`}>
                                            {badge.label}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </main>
        </div>
    );
};

export default TrialsPage;
